import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Minus, Plus, ShoppingBag, PenTool, Layers, Ban, UploadCloud, X, Loader2, FileImage, Sparkles, ZoomIn, Repeat, Zap } from "lucide-react";
import { toast } from "sonner";
import { api, fmt, API_BASE, formatApiError, trackClicSoumission } from "../lib/api";
import { unitPrice, currentTier, currentPriceTier } from "../lib/pricing";
import { SEO } from "../components/SEO";
import { categoryKeywords, slugify } from "../lib/seoKeywords";
import { lp, catalogueLabel, productAlt } from "../lib/localize";
import { useCart } from "../context/CartContext";
import { useLang } from "../i18n/LanguageContext";
import { FadeUp } from "../components/motion";
import { ELink } from "../components/Editable";
import ImageLightbox from "../components/ImageLightbox";
import ProductReviews from "../components/ProductReviews";
import PersoTextarea from "../components/PersoTextarea";
import { GoogleRatingBadge } from "../components/GoogleRatingBadge";
import { ShareButtons } from "../components/ShareButtons";
import { useLogoUpload } from "../lib/useLogoUpload";
import { trackViewContent } from "../lib/pixels";

export default function ProductDetail() {
  const { id } = useParams();
  const { add } = useCart();
  const { lang, t, ts } = useLang();
  const [product, setProduct] = useState(null);
  const [revStats, setRevStats] = useState(null);  // note/nombre d'avis réels → aggregateRating du schéma Product
  const [zoomOpen, setZoomOpen] = useState(false);
  const [sizeId, setSizeId] = useState("s");
  const [finishId, setFinishId] = useState(null);
  const [qty, setQty] = useState(1);
  const minQty = product?.min_qty || 1;

  // Date de production estimée : 5 jours ouvrables (sam/dim exclus)
  const readyDate = useMemo(() => {
    const d = new Date();
    let added = 0;
    while (added < 5) {
      d.setDate(d.getDate() + 1);
      if (d.getDay() !== 0 && d.getDay() !== 6) added++;
    }
    return d.toLocaleDateString(lang === "en" ? "en-CA" : "fr-CA",
      { timeZone: "America/Montreal", weekday: "long", day: "numeric", month: "long" });
  }, [lang]);
  const [inscType, setInscType] = useState("gravure");
  const [inscText, setInscText] = useState("");
  const { logo, setLogo, uploading, fileInput, uploadLogo } = useLogoUpload(t);
  // taper un texte avec « Sans inscription » sélectionné → bascule en impression (prix cohérent)
  const onInscTextChange = (v) => {
    const maxLines = product?.max_lines || 0;  // ex. plaques d'identification : 4 lignes max
    if (maxLines > 0) {
      const lignes = v.split("\n");
      if (lignes.length > maxLines) v = lignes.slice(0, maxLines).join("\n");
    }
    setInscText(v);
    if (inscType === "aucune" && v.trim()) setInscType(product?.default_inscription || "impression");
  };
  const [atelierNote, setAtelierNote] = useState("");  // note pour l'atelier (par article)
  const [color, setColor] = useState("");  // couleur choisie (variantes, ex. Urbano)
  const [activeImg, setActiveImg] = useState(0);  // photo affichée dans la galerie
  const gallery = product ? [...new Set([product.image, product.image_ruban, product.image_insert, ...(product.images || [])].filter(Boolean))] : [];
  const colorList = product ? ((lang === "en" ? product.colors_en : product.colors) || []) : [];
  // Galerie synchronisée : choisir une couleur affiche sa photo (produits dont
  // images[] suit l'ordre des couleurs — ex. plaques d'identification)
  const pickColor = (v) => {
    setColor(v);
    const gi = colorList.indexOf(v) + 1;  // +1 : la 1re photo de la galerie est l'image principale
    if (product?.images?.length === colorList.length && gallery[gi]) setActiveImg(gi);
  };
  const isUrbano = product?.catalogue_no === "5.0";  // produits promotionnels : pas de choix gravure/impression
  const qtyMax = product?.stock ?? (isUrbano ? 99999 : 1000);
  const maxTierQty = isUrbano && product?.price_tiers?.length
    ? Math.max(...product.price_tiers.map((tr) => tr.min_qty)) : null;
  const [occasion, setOccasion] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  // Popup « Demande de soumission » : quantités > 100 (politique 2026-09-18)
  const [quoteOpen, setQuoteOpen] = useState(false);
  const [qForm, setQForm] = useState({ first_name: "", last_name: "", email: "", qty: 101, message: "" });
  const [qBusy, setQBusy] = useState(false);
  const openQuote = () => {
    setQForm((f) => ({ ...f, qty: Math.max(qty, 101) }));
    setQuoteOpen(true);
  };
  const submitQuote = async (e) => {
    e.preventDefault();
    setQBusy(true);
    try {
      await api.post("/contact", {
        first_name: qForm.first_name, last_name: qForm.last_name, email: qForm.email,
        message: `${lang === "en" ? "Quote request" : "Demande de soumission"} — ${lp(product, lang).name} — ${
          lang === "en" ? "quantity" : "quantité"} : ${qForm.qty}\n\n${qForm.message}`.trim(),
        references: product.sku || "",
      });
      toast.success(t("contact.sent"));
      setQuoteOpen(false);
      setQForm({ first_name: "", last_name: "", email: "", qty: 101, message: "" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setQBusy(false);
    }
  };
  const [aiLoading, setAiLoading] = useState(false);

  useEffect(() => {
    api.get(`/products/${id}`).then((res) => {
      setProduct(res.data);
      trackViewContent(res.data);
      // URL canonique : un ancien lien « tx-… » est remplacé par « cat6-… » dans la barre d'adresse
      if (res.data?.id && res.data.id !== id) {
        window.history.replaceState(null, "", `/produit/${res.data.id}`);
      }
      if (res.data?.min_qty > 1) {  // produits en vrac (ex. Urbano) : quantité minimale + impression par défaut
        setQty(res.data.min_qty);
        setInscType("impression");
      }
      const cols = res.data?.colors || [];
      if (cols.length) setColor(cols[0]);  // couleur par défaut = 1re variante
      setSizeId(res.data.sizes[0].id);
      setFinishId((res.data.finishes || [])[0]?.id || null);
      // Avis clients réels de la fiche → aggregateRating du schéma Product
      // (étoiles dans les résultats enrichis — demande du propriétaire, 2026-10-06)
      api.get(`/products/${res.data.id}/reviews`).then((r) => {
        if (r.data?.count > 0 && r.data?.average) setRevStats({ avg: r.data.average, count: r.data.count });
      }).catch(() => {});
    }).catch(() => setProduct(false));
  }, [id]);

  const unit = useMemo(
    () => (product ? unitPrice(product, sizeId, qty, inscType, finishId) : 0),
    [product, sizeId, finishId, qty, inscType]
  );
  const tier = product ? currentTier(product, qty) : null;
  const priceTier = product ? currentPriceTier(product, qty) : null;

  const INSCRIPTION_TYPES = [
    { id: "gravure", label: t("product.gravure"), icon: PenTool, hint: t("product.gravure_hint") },
    // Impression couleur masquée pour certains produits (ex. médailles d'animaux : gravure seulement)
    ...(product?.allow_impression === false
      ? []
      : [{ id: "impression", label: t("product.impression"), icon: Layers, hint: t("product.impression_hint") }]),
    // Inscription arrière : offerte seulement si un prix est défini sur la fiche (admin)
    ...((product?.inscription_fees?.arriere || 0) > 0
      ? [{ id: "arriere", label: t("product.arriere"), icon: Repeat, hint: t("product.arriere_hint") }]
      : []),
    { id: "aucune", label: t("product.aucune"), icon: Ban, hint: t("product.aucune_hint") },
  ];

  const suggestEngraving = async () => {
    if (occasion.trim().length < 3) return;
    setAiLoading(true);
    try {
      const { data } = await api.post("/ai/engraving-suggestions", {
        occasion, product_name: loc.name, lang,
      });
      setSuggestions(data.suggestions || []);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setAiLoading(false);
    }
  };

  if (product === null) return <div className="pt-40 text-center text-ivory/70">{t("product.loading")}</div>;
  if (product === false) return (
    <div data-testid="product-not-found" className="min-h-[60vh] flex flex-col items-center justify-center px-6 pt-24 pb-16 text-center">
      <h1 className="font-serif text-4xl mb-8">{t("product.not_found")}</h1>
      <ELink editPath="link.product_404" to="/boutique" className="inline-flex items-center gap-3 bg-gold text-ink font-semibold px-8 py-4 rounded-full hover:bg-champagne transition-colors duration-300">
        {t("product.back")}
      </ELink>
    </div>
  );

  const logoIsImage = logo && !logo.name.toLowerCase().endsWith(".pdf");
  const loc = lp(product, lang);  // la photo affichée suit le fini sélectionné (image de la variante)
  const curFinish = (product.finishes || []).find((f) => f.id === finishId);
  const displayImage = curFinish?.image || gallery[activeImg] || product.image;
  const sizeLabel = (sid) => {
    const s = product.sizes.find((x) => x.id === sid);
    return s ? loc.sizeLabel(s) : "";
  };

  const addToCart = () => {
    // panier ouvert à tous (comme la fiche rapide) — le compte est exigé à la caisse
    const perso = inscType !== "aucune" || minQty > 1 || !!logo || !!inscText.trim();  // personnalisation offerte sur tous les produits (comme Urbano)
    const effType = perso ? (inscType === "aucune" ? (product.default_inscription || "impression") : inscType) : "aucune";
    add({
      product_id: product.id, name: product.name, name_en: product.name_en || "", image: displayImage,
      size_id: sizeId, size_label: product.sizes.find((s) => s.id === sizeId).label,
      size_label_en: product.sizes.find((s) => s.id === sizeId).label_en || "",
      finish_id: finishId || "",
      finish_label: (product.finishes || []).find((f) => f.id === finishId)?.label || "",
      finish_label_en: (product.finishes || []).find((f) => f.id === finishId)?.label_en || "",
      qty, inscription_type: effType, inscription_text: perso ? inscText.trim() : "",
      logo_path: perso ? logo?.path || "" : "",
      logo_name: perso ? logo?.name || "" : "",
      logo_url: perso ? logo?.url || "" : "",
      note: atelierNote.trim(),
      color,
      pricing: { base_price: product.base_price, sizes: product.sizes, tiers: product.tiers, inscription_fees: product.inscription_fees, price_tiers: product.price_tiers || [], finishes: product.finishes || [], min_qty: product.min_qty || 1 },
    });
    toast.success(t("product.added_toast", { name: loc.name }));
  };

  const productLd = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: loc.name,
    image: gallery.map((src) => (src.startsWith("http") ? src : `https://mrtrophee.ca${src}`)),
    description: loc.description || loc.tagline,
    // Étoiles dans les résultats Google : note moyenne des avis RÉELS de la fiche
    ...(revStats ? {
      aggregateRating: {
        "@type": "AggregateRating", ratingValue: revStats.avg,
        reviewCount: revStats.count, bestRating: 5, worstRating: 1,
      },
    } : {}),
    sku: product.sku || product.id,
    brand: { "@type": "Brand", name: "Mr Trophée" },
    offers: {
      "@type": "Offer",
      priceCurrency: "CAD",
      price: product.base_price,
      availability: product.stock === 0 ? "https://schema.org/OutOfStock" : "https://schema.org/InStock",
      url: `https://mrtrophee.ca/produit/${product.id}`,
    },
  };
  const breadcrumbLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: lang === "en" ? "Home" : "Accueil", item: "https://mrtrophee.ca/" },
      { "@type": "ListItem", position: 2, name: lang === "en" ? "The Shop" : "La Boutique", item: "https://mrtrophee.ca/boutique" },
      { "@type": "ListItem", position: 3, name: loc.name },
    ],
  };

  return (
    <div data-testid="product-detail-page" className="max-w-7xl mx-auto px-6 lg:px-10 pt-28 pb-24">
      <SEO
        title={`${loc.name} ${ts("seo.product_suffix")}`}
        description={(loc.description || loc.tagline || "").slice(0, 155)}
        path={`/produit/${product.id}`}
        image={product.image}
        jsonLd={[productLd, breadcrumbLd]}
        keywords={categoryKeywords(product.category, lang)}
      />
      <ELink editPath="link.product_back" to="/boutique" data-testid="back-to-catalogue" className="inline-flex items-center gap-2 text-sm text-ivory/60 hover:text-gold transition-colors duration-300 mb-10">
        <ArrowLeft className="w-4 h-4" /> {t("product.back")}
      </ELink>

      {/* Bandeau visible UNIQUEMENT pour un admin : la fiche d'un produit désactivé
          n'est servie qu'aux administrateurs connectés (404 pour les visiteurs) */}
      {product.active === false && (
        <div data-testid="admin-preview-banner"
          className="mb-8 border border-amber-400/50 bg-amber-400/10 text-amber-300 text-sm rounded-xl px-5 py-3.5">
          {t("product.admin_preview")}
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-12 lg:gap-20">
        {/* Visuel + aperçu gravure */}
        <FadeUp>
          <div className="relative card-clip overflow-hidden bg-surface spotlight group">
            {displayImage ? (
              <>
                <button type="button" onClick={() => setZoomOpen(true)} data-testid="product-image-zoom"
                  aria-label={ts("product.zoom_image")}
                  className="block w-full cursor-zoom-in">
                  <img src={displayImage} alt={productAlt(product, lang)} className="w-full h-[480px] lg:h-[560px] object-contain bg-[#f4f2ec]" />
                </button>
                <span className="absolute bottom-3 right-3 w-9 h-9 rounded-full bg-ink/70 text-gold border border-gold/40 flex items-center justify-center pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <ZoomIn className="w-4 h-4" />
                </span>
              </>
            ) : (
              <div className="w-full h-[480px] lg:h-[560px] bg-[#f4f2ec] flex items-center justify-center">
                <p className="text-ink/30 text-sm" data-testid="product-image-empty">{t("product.image_pending")}</p>
              </div>
            )}
          </div>
          {/* Galerie : photos supplémentaires (ex. produits Urbano) */}
          {gallery.length > 1 && !curFinish?.image && (
            <div className="flex gap-3 mt-4 overflow-x-auto pb-1" data-testid="product-gallery">
              {gallery.map((src, gi) => (
                <button key={src} type="button" onClick={() => setActiveImg(gi)}
                  data-testid={`gallery-thumb-${gi}`}
                  className={`shrink-0 w-20 h-20 rounded-lg bg-[#f4f2ec] p-1.5 border-2 transition-colors duration-300 ${gi === activeImg ? "border-gold" : "border-ivory/15 hover:border-gold/50"}`}>
                  <img src={src} alt={`${productAlt(product, lang)} — ${gi + 1}`} loading="lazy" className="w-full h-full object-contain" />
                </button>
              ))}
            </div>
          )}
        </FadeUp>

        {/* Configurateur */}
        <FadeUp delay={0.1}>
          <p className="text-xs tracking-[0.3em] uppercase text-gold/70 mb-2">{loc.category}</p>
          <h1 className="font-serif text-4xl md:text-5xl mb-3">{loc.name}</h1>
          <p className="text-champagne italic font-serif text-lg mb-4">{loc.tagline}</p>
          {(product.catalogue_no || product.sku) && (
            <p className="text-xs text-ivory/65 tracking-wide mb-4" data-testid="product-ref">
              {product.catalogue_no && <>{catalogueLabel(product.catalogue_no)}</>}
              {product.catalogue_no && product.sku && " · "}
              {product.sku && <>Code : {product.sku}</>}
            </p>
          )}
          <div className="mb-6 flex flex-wrap items-center gap-4">
            <GoogleRatingBadge />
            <ShareButtons title={`${loc.name} — Mr Trophée`} />
          </div>
          <p className="text-ivory/60 leading-relaxed mb-10">{loc.description}</p>

          {/* Grandeur */}
          <div className="mb-8">
            <h3 className="text-sm tracking-widest uppercase text-ivory/70 mb-3">{t("product.size_title")}</h3>
            <div className="flex flex-wrap gap-3" data-testid="size-selector">
              {product.sizes.map((s) => (
                <button
                  key={s.id}
                  data-testid={`size-${s.id}`}
                  onClick={() => setSizeId(s.id)}
                  className={`px-5 py-2.5 rounded-full text-sm border transition-colors duration-300 ${
                    sizeId === s.id ? "bg-gold text-ink border-gold font-semibold" : "border-ivory/20 text-ivory/70 hover:border-gold/60 hover:text-gold"
                  }`}
                >
                  {loc.sizeLabel(s)}
                </button>
              ))}
            </div>
          </div>

          {/* Couleur (variantes, ex. produits promotionnels Urbano) */}
          {colorList.length > 1 && (
            <div className="mb-8">
              <h3 className="text-sm tracking-widest uppercase text-ivory/70 mb-3">{t("product.color_title")}</h3>
              <select
                data-testid="color-select"
                value={color}
                onChange={(e) => pickColor(e.target.value)}
                className="bg-surface border border-ivory/15 rounded-lg px-4 py-3 text-sm text-ivory focus:border-gold focus:outline-none transition-colors duration-300 min-w-56"
              >
                {colorList.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
          )}

          {/* Fiches individuelles par couleur (demande du propriétaire) : chaque
              couleur de la forme choisie mène à sa fiche détaillée (photo de l'item) */}
          {product?.variant_fiche_pattern && finishId && colorList.length > 1 && (
            <div className="mb-8">
              <h3 className="text-sm tracking-widest uppercase text-ivory/70 mb-3">{t("product.color_fiches_title")}</h3>
              <div className="flex flex-wrap gap-2" data-testid="color-fiche-links">
                {colorList.map((c) => {
                  const fid = product.variant_fiche_pattern
                    .replace("{forme}", finishId)
                    .replace("{couleur}", slugify(c));
                  const actif = c === color;
                  return (
                    <Link key={c} to={`/produit/${fid}`} data-testid={`color-fiche-${slugify(c)}`}
                      className={`rounded-full px-4 py-2 text-xs border transition-colors duration-300 ${actif
                        ? "bg-gold text-ink border-gold font-semibold"
                        : "border-ivory/15 text-ivory/70 hover:border-gold/50 hover:text-gold"}`}>
                      {c}
                    </Link>
                  );
                })}
              </div>
            </div>
          )}

          {/* Fini */}
          {(product.finishes?.length || 0) > 1 && (
            <div className="mb-8">
              <h3 className="text-sm tracking-widest uppercase text-ivory/70 mb-3">{(lang === "en" ? product.finish_title_en : product.finish_title) || t("product.finish_title")}</h3>
              {product.finishes.length > 5 ? (
                <select value={finishId || ""} onChange={(e) => setFinishId(e.target.value)}
                  data-testid="finish-select"
                  className="w-full max-w-md bg-ink border border-ivory/15 rounded-lg px-4 py-3 text-sm text-ivory focus:border-gold focus:outline-none">
                  {product.finishes.map((f) => (
                    <option key={f.id} value={f.id} data-testid={`finish-option-${f.id}`}>
                      {(lang === "en" && f.label_en ? f.label_en : f.label)}{f.extra > 0 ? ` +${fmt(f.extra)}` : ""}
                    </option>
                  ))}
                </select>
              ) : (
                <div className="flex flex-wrap gap-3" data-testid="finish-selector">
                  {product.finishes.map((f) => (
                    <button
                      key={f.id}
                      data-testid={`finish-${f.id}`}
                      onClick={() => setFinishId(f.id)}
                      className={`px-5 py-2.5 rounded-full text-sm border transition-colors duration-300 ${
                        finishId === f.id ? "bg-gold text-ink border-gold font-semibold" : "border-ivory/20 text-ivory/70 hover:border-gold/60 hover:text-gold"
                      }`}
                    >
                      {lang === "en" && f.label_en ? f.label_en : f.label}
                      {f.extra > 0 && <span className="ml-1.5 text-xs opacity-70">+{fmt(f.extra)}</span>}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Quantité + paliers */}
          <div className="mb-8">
            <h3 className="text-sm tracking-widest uppercase text-ivory/70 mb-3">{t("product.qty_title")}</h3>
            <div className="flex items-center gap-4 mb-4">
              <button data-testid="qty-minus" onClick={() => setQty((q) => Math.max(minQty, q - 1))} className="w-10 h-10 rounded-full border border-ivory/20 flex items-center justify-center hover:border-gold hover:text-gold transition-colors duration-300">
                <Minus className="w-4 h-4" />
              </button>
              <input data-testid="qty-value" type="number" min={minQty} max={qtyMax} value={qty}
                onChange={(e) => {
                  const v = parseInt(e.target.value);
                  if (!Number.isNaN(v) && v > qtyMax) toast.info(t("product.stock_capped", { n: qtyMax }));
                  setQty(Number.isNaN(v) ? minQty : Math.max(minQty, Math.min(qtyMax, v)));
                }}
                className="font-serif text-3xl w-20 text-center bg-transparent border-b border-ivory/20 focus:border-gold focus:outline-none text-ivory [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none" />
              <button data-testid="qty-plus" onClick={() => setQty((q) => Math.min(qtyMax, q + 1))} className="w-10 h-10 rounded-full border border-ivory/20 flex items-center justify-center hover:border-gold hover:text-gold transition-colors duration-300">
                <Plus className="w-4 h-4" />
              </button>
              {minQty > 1 && (
                <span data-testid="min-qty-hint" className="text-xs text-gold border border-gold/30 px-3 py-1.5 rounded-full">
                  {t("product.min_qty", { n: minQty })}
                </span>
              )}
              {product?.stock != null && (
                <span data-testid="stock-left" className="text-xs text-ivory/70 border border-ivory/15 px-3 py-1.5 rounded-full">
                  {t("product.stock_left", { n: product.stock })}
                </span>
              )}
              {priceTier ? (
                <span data-testid="tier-badge" className="text-xs bg-gold/15 text-gold border border-gold/30 px-3 py-1.5 rounded-full">
                  {t("product.tier_applied_price", { min: priceTier.min_qty, price: fmt(priceTier.price) })}
                </span>
              ) : tier && (
                <span data-testid="tier-badge" className="text-xs bg-gold/15 text-gold border border-gold/30 px-3 py-1.5 rounded-full">
                  {t("product.tier_applied", { pct: Math.round(tier.discount * 100) })}
                </span>
              )}
              {/* Rabais UNIQUE (demande du propriétaire, 2026-10-22) : « X % » ou
                  « X % dès Y unités » — résolu côté serveur (produit → catégorie →
                  grande catégorie → catalogue) et reflété dans le prix affiché */}
              {!tier && !priceTier && product?.discount_rule?.pct > 0 && (() => {
                const rg = product.discount_rule;
                const seuil = Math.max(1, rg.min_qty || 0);
                const atteint = qty >= seuil;
                return (
                  <span data-testid="discount-rule-badge"
                    className={`text-xs px-3 py-1.5 rounded-full border ${
                      atteint ? "bg-gold/15 text-gold border-gold/30 font-bold" : "text-ivory/55 border-ivory/15"}`}>
                    {atteint
                      ? (lang === "en" ? `−${rg.pct}% discount applied` : `Rabais −${rg.pct} % appliqué`)
                      : (lang === "en" ? `−${rg.pct}% from ${seuil} units` : `Rabais −${rg.pct} % dès ${seuil} unités`)}
                  </span>
                );
              })()}
            </div>
            {/* Grille de prix dégressifs : rangez visibles, rangée active surlignée selon la quantité */}
            {(product.price_tiers?.length > 0 || product.tiers?.length > 0) && (() => {
              const sizeExtra = (product.sizes.find((s) => s.id === sizeId) || product.sizes[0] || {}).extra || 0;
              const finishExtra = (product.finishes || []).find((f) => f.id === finishId)?.extra
                ?? (product.finishes || [])[0]?.extra ?? 0;
              const rows = [];
              if (product.price_tiers?.length) {
                const sorted = [...product.price_tiers].sort((a, b) => a.min_qty - b.min_qty);
                const base = sorted[0].price;
                sorted.forEach((tr, i) => {
                  const next = sorted[i + 1];
                  const unitRow = Math.round((tr.price + sizeExtra + finishExtra) * 100) / 100;
                  rows.push({ min: tr.min_qty, max: next ? next.min_qty - 1 : null,
                    unit: unitRow, pct: base > 0 ? Math.round((1 - tr.price / base) * 10000) / 100 : 0 });
                });
              } else {
                const sorted = [...(product.tiers || [])].sort((a, b) => a.min_qty - b.min_qty);
                const baseUnit = (product.base_price || 0) + sizeExtra + finishExtra;
                if (sorted[0].min_qty > minQty) {
                  rows.push({ min: minQty, max: sorted[0].min_qty - 1, unit: baseUnit, pct: 0 });
                }
                sorted.forEach((tr, i) => {
                  const next = sorted[i + 1];
                  rows.push({ min: tr.min_qty, max: next ? next.min_qty - 1 : null,
                    unit: Math.round(baseUnit * (1 - tr.discount) * 100) / 100,
                    pct: Math.round(tr.discount * 10000) / 100 });
                });
              }
              return (
                <div className="border border-ivory/12 rounded-lg overflow-hidden" data-testid="tier-table">
                  <p className="px-4 pt-3 pb-2 text-[11px] uppercase tracking-widest text-gold">{t("product.tier_grid_title")}</p>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-[10px] uppercase tracking-widest text-ivory/50 border-t border-b border-ivory/10">
                        <th className="px-4 py-2 font-medium">{t("product.tier_grid_qty")}</th>
                        <th className="px-4 py-2 font-medium text-right">{t("product.tier_grid_unit")}</th>
                        <th className="px-4 py-2 font-medium text-right">{t("product.tier_grid_discount")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((r) => {
                        const active = qty >= r.min && (r.max == null || qty <= r.max);
                        return (
                          <tr key={r.min} data-testid={`tier-row-${r.min}`} {...(active ? { "data-testid-active": "true" } : {})}
                            className={`border-b border-ivory/5 last:border-0 transition-colors duration-300 ${active ? "bg-gold/15" : ""}`}>
                            <td className={`px-4 py-2.5 ${active ? "text-gold font-bold" : "text-ivory/80"}`}>
                              {r.max != null ? `${r.min} – ${r.max}` : `${r.min}+`}
                              {active && <span className="ml-2 text-[10px] uppercase tracking-widest">●</span>}
                            </td>
                            <td className={`px-4 py-2.5 text-right ${active ? "text-gold font-bold" : "text-ivory/80"}`}>
                              {fmt(r.unit)} / {t("product.unit_suffix")}
                            </td>
                            <td className={`px-4 py-2.5 text-right ${r.pct > 0 ? (active ? "text-gold font-bold" : "text-emerald-400") : "text-ivory/45"}`}>
                              {r.pct > 0 ? `−${r.pct.toLocaleString(lang === "en" ? "en-CA" : "fr-CA", { maximumFractionDigits: 2 })} %` : t("product.tier_grid_base")}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  {/* Au-delà de 100 : prix sur mesure — rangée cliquable vers le popup
                      « Demande de soumission » (politique 2026-09-18) */}
                  <button type="button" onClick={openQuote} data-testid="tier-quote-cta"
                    className="w-full flex items-center justify-between px-4 py-2.5 text-sm border-t border-gold/30 bg-gold/5 hover:bg-gold/15 transition-colors duration-300 cursor-pointer">
                    <span className="text-ivory/80 font-medium">&gt; 100</span>
                    <span className="text-gold font-bold uppercase tracking-widest text-[11px]">
                      {t("product.tier_grid_quote")} →
                    </span>
                  </button>
                  <p className="px-4 py-2 text-[10px] text-ivory/45">{t("product.tier_grid_fee_note")}</p>
                </div>
              );
            })()}
            <p className="text-xs text-ivory/70 mt-3" data-testid="bulk-note">
              {t("product.bulk_note")}{" "}
              <ELink editPath="link.product_bulk"
                to={`/contact?produit=${encodeURIComponent(lang === "en" ? (product.name_en || product.name) : product.name)}`}
                data-testid="bulk-note-link" className="text-gold hover:text-champagne underline underline-offset-2 transition-colors duration-300">
                {t("product.bulk_note_link")}
              </ELink>
            </p>
            {qty > ((isUrbano && maxTierQty) ? Math.min(maxTierQty, 100) : 100) && (
              <div className="mt-4 border border-gold/40 bg-gold/10 rounded-lg p-5" data-testid="quote-box">
                <p className="font-serif text-lg text-gold mb-1">{t("product.quote_title")}</p>
                <p className="text-sm text-ivory/60 mb-3">
                  {t("product.quote_note", { n: (isUrbano && maxTierQty) ? Math.min(maxTierQty, 100) : 100 })}
                </p>
                <button type="button" onClick={openQuote}
                  data-testid="quote-cta"
                  className="inline-flex items-center gap-2 bg-gold text-ink px-5 py-2.5 rounded-full text-sm font-semibold hover:bg-champagne transition-colors duration-300 cursor-pointer"
                >
                  {t("product.quote_cta")}
                </button>
              </div>
            )}
          </div>
          <div className="mb-8">
            <h3 className="text-sm tracking-widest uppercase text-ivory/70 mb-3">{t("product.insc_title")}</h3>
            {!isUrbano && (
            <div className="grid sm:grid-cols-3 gap-3 mb-4" data-testid="inscription-selector">
              {INSCRIPTION_TYPES.filter((ty) => !(minQty > 1 && ty.id === "aucune")).map((ty) => (
                <button
                  key={ty.id}
                  data-testid={`inscription-${ty.id}`}
                  onClick={() => setInscType(ty.id)}
                  className={`text-left p-4 rounded-lg border transition-colors duration-300 ${
                    inscType === ty.id ? "border-gold bg-gold/10" : "border-ivory/15 hover:border-gold/50"
                  }`}
                >
                  <ty.icon className={`w-5 h-5 mb-2 ${inscType === ty.id ? "text-gold" : "text-ivory/70"}`} />
                  <p className="text-sm font-semibold">{ty.label}</p>
                  <p className="text-xs text-ivory/70 mt-1">{ty.hint}</p>
                  {/* Prix de personnalisation non affichés (décision du propriétaire
                      2026-10-09) : « à déterminer » — confirmés dans la soumission */}
                  {ty.id !== "aucune" && <p className="text-xs text-champagne mt-1">{t("product.fee_tbd")}</p>}
                </button>
              ))}
            </div>
            )}
            <p className="text-xs text-gold/80 mb-4" data-testid="matrice-fee">{t("product.matrice_fee")}</p>
            {/* Personnalisation offerte sur tous les produits (comme Urbano) : texte 3 lignes min + logo + idées IA */}
            {(
              <>
                <PersoTextarea
                  data-testid="inscription-text"
                  value={inscText}
                  onChange={onInscTextChange}
                  maxLength={200}
                  placeholder={ts("product.text_placeholder")}
                  className="w-full bg-surface border border-ivory/15 rounded-lg px-4 py-3 text-sm text-ivory placeholder:text-ivory/60 focus:border-gold focus:outline-none transition-colors duration-300 overflow-hidden"
                />
                <p className="text-xs text-ivory/60 mt-2" data-testid="inscription-lines-hint">
                  {t("product.text_lines_hint")}
                </p>
                {product?.max_lines > 0 && (
                  <p data-testid="inscription-max-lines" className="text-xs text-gold mt-2">
                    {t("product.text_lines_max", { n: product.max_lines })}
                  </p>
                )}
                <div className="mt-3 bg-surface border border-ivory/10 rounded-lg p-4" data-testid="ai-engraving">
                  <p className="text-xs tracking-widest uppercase text-gold mb-2 inline-flex items-center gap-1.5">
                    <Sparkles className="w-3.5 h-3.5" /> {t("product.ai_kicker")}
                  </p>
                  <div className="flex gap-2">
                    <input
                      data-testid="ai-occasion"
                      value={occasion}
                      onChange={(e) => setOccasion(e.target.value)}
                      placeholder={ts("product.ai_placeholder")}
                      className="flex-1 min-w-0 bg-ink border border-ivory/15 rounded-lg px-3 py-2.5 text-sm text-ivory placeholder:text-ivory/60 focus:border-gold focus:outline-none transition-colors duration-300"
                    />
                    <button
                      type="button" data-testid="ai-suggest" disabled={aiLoading || occasion.trim().length < 3}
                      onClick={suggestEngraving}
                      className="inline-flex items-center gap-2 border border-gold/40 text-gold px-4 py-2.5 rounded-lg text-sm hover:bg-gold hover:text-ink transition-colors duration-300 disabled:opacity-50 whitespace-nowrap shrink-0"
                    >
                      {aiLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                      {t("product.ai_button")}
                    </button>
                  </div>
                  {suggestions.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-3" data-testid="ai-suggestions">
                      {suggestions.map((s, i) => (
                        <button
                          key={i} type="button" data-testid={`ai-suggestion-${i}`}
                          onClick={() => setInscText(s)}
                          className="text-xs border border-ivory/20 text-ivory/80 px-3 py-2 rounded-full hover:border-gold hover:text-gold transition-colors duration-300 text-left"
                        >
                          {s}
                        </button>
                      ))}
                      <p className="w-full text-[11px] text-ivory/65">{t("product.ai_note")}</p>
                    </div>
                  )}
                </div>
                {/* Logo */}
                <div className="mt-4">
                  <h4 className="text-xs tracking-widest uppercase text-ivory/60 mb-2">{t("product.logo_title")}</h4>
                  <input ref={fileInput} type="file" accept=".png,.jpg,.jpeg,.svg,.pdf,.webp" className="hidden" data-testid="logo-input"
                    onChange={(e) => uploadLogo(e, () => { if (inscType === "aucune") setInscType(product?.default_inscription || "impression"); })} />
                  {logo ? (
                    <div className="flex items-center gap-3 bg-surface border border-gold/30 rounded-lg px-4 py-3" data-testid="logo-chip">
                      {logoIsImage
                        ? <img src={`${API_BASE}${logo.url}`} alt="" className="h-8 w-8 object-contain rounded" />
                        : <FileImage className="w-6 h-6 text-gold" />}
                      <span className="text-sm text-ivory/80 truncate flex-1">{logo.name}</span>
                      <button data-testid="logo-remove" onClick={() => setLogo(null)} className="text-ivory/65 hover:text-red-400 transition-colors duration-300">
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <button
                      data-testid="logo-upload-button"
                      onClick={() => fileInput.current?.click()}
                      disabled={uploading}
                      className="w-full flex items-center justify-center gap-3 border border-dashed border-ivory/25 rounded-lg px-4 py-4 text-sm text-ivory/60 hover:border-gold hover:text-gold transition-colors duration-300 disabled:opacity-60"
                    >
                      {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <UploadCloud className="w-5 h-5" />}
                      {uploading ? t("product.logo_uploading") : t("product.logo_cta")}
                    </button>
                  )}
                  <p className="text-xs text-ivory/60 mt-2">{t("product.logo_formats")}</p>
                </div>
              </>
            )}
          </div>

          {/* Avis frais d'écran / répétition (produits avec charges fixes, ex. Urbano) */}
          {product.screen_charge > 0 && (
            <div className="mb-8 bg-gold/5 border border-gold/25 rounded-lg px-4 py-3.5" data-testid="setup-fee-note">
              <p className="text-xs text-ivory/70 leading-relaxed">
                {t("product.setup_fee_note", {
                  s: fmt(product.screen_charge),
                  r: fmt(product.repeat_charge || product.screen_charge),
                })}
              </p>
            </div>
          )}

          {/* Note pour l'atelier */}
          <div className="mb-8">
            <h3 className="text-sm tracking-widest uppercase text-ivory/70 mb-3">{t("product.atelier_note_title")}</h3>
            <textarea
              data-testid="atelier-note"
              value={atelierNote}
              onChange={(e) => setAtelierNote(e.target.value)}
              maxLength={500}
              rows={2}
              placeholder={ts("product.atelier_note_placeholder")}
              className="w-full bg-surface border border-ivory/15 rounded-lg px-4 py-3 text-sm text-ivory placeholder:text-ivory/60 focus:border-gold focus:outline-none transition-colors duration-300"
            />
          </div>

          {/* Prix + CTA */}
          <div className="flex items-end justify-between border-t border-gold/15 pt-6 mb-6">
            <div>
              <p className="text-xs text-ivory/70 uppercase tracking-widest mb-1">{t("product.unit_price")}</p>
              <p data-testid="unit-price" className="font-serif text-3xl text-champagne">{fmt(unit)}</p>
            </div>
            <div className="text-right">
              <p className="text-xs text-ivory/70 uppercase tracking-widest mb-1">{t("product.total", { qty })}</p>
              <p data-testid="total-price" className="font-serif text-4xl text-gold">{fmt(unit * qty)}</p>
            </div>
          </div>
          <button
            data-testid="add-to-cart-button"
            onClick={addToCart}
            className="w-full group inline-flex items-center justify-center gap-3 bg-gold text-ink font-semibold px-8 py-4 rounded-full hover:bg-champagne transition-colors duration-300"
          >
            <ShoppingBag className="w-5 h-5" /> {t("product.add_to_cart")}
          </button>
          {/* Soumission en 30 secondes directement depuis la fiche produit
              (demande du propriétaire, 2026-10-17) — contour or pour ne pas
              concurrencer le bouton d'ajout au panier ; PRÉ-REMPLIE du nom du
              produit (?produit=…) à l'arrivée sur la soumission rapide */}
          <Link
            to={`/soumission-rapide?produit=${encodeURIComponent(lang === "en" ? (product.name_en || product.name) : product.name)}&qte=${qty}`}
            data-testid="product-quick-quote"
            onClick={() => trackClicSoumission("fiche")}
            className="mt-3 w-full group inline-flex items-center justify-center gap-3 border border-gold/50 text-gold font-semibold px-8 py-4 rounded-full hover:bg-gold hover:text-ink hover:shadow-[0_0_35px_rgba(212,175,55,0.35)] transition-all duration-300"
          >
            <Zap className="w-5 h-5 transition-transform duration-300 group-hover:-rotate-12" /> {t("quote.fab_long")}
          </Link>
          <p className="text-xs text-ivory/70 mt-4 text-center" data-testid="ready-by-note">
            {t("product.ready_by", { date: readyDate })}
          </p>
        </FadeUp>
      </div>
      <ProductReviews productId={id} />
      {zoomOpen && displayImage && (
        <ImageLightbox src={displayImage} alt={productAlt(product, lang)} onClose={() => setZoomOpen(false)}
          testid="product-lightbox"
          labels={{ zoomIn: t("product.zoom_in"), zoomOut: t("product.zoom_out"), reset: t("product.zoom_reset"), close: t("product.zoom_close") }} />
      )}

      {/* Popup « Demande de soumission » (> 100 unités ou rangée >100 de la grille) */}
      {quoteOpen && (
        <div className="fixed inset-0 z-[80] bg-ink/85 backdrop-blur-sm flex items-center justify-center p-6"
          onClick={() => setQuoteOpen(false)} data-testid="quote-popup">
          <div className="bg-surface border border-gold/30 rounded-2xl p-7 max-w-md w-full relative shadow-[0_0_40px_rgba(212,175,55,0.15)]"
            onClick={(e) => e.stopPropagation()}>
            <button type="button" onClick={() => setQuoteOpen(false)} data-testid="quote-popup-close"
              className="absolute top-3 right-3 text-ivory/50 hover:text-gold transition-colors duration-200 cursor-pointer">
              <X className="w-5 h-5" />
            </button>
            <h3 className="font-serif text-2xl text-gold mb-1">{t("product.quote_popup_title")}</h3>
            <p className="text-xs text-ivory/55 mb-5">{loc.name}</p>
            <form onSubmit={submitQuote} className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <input required value={qForm.first_name}
                  onChange={(e) => setQForm({ ...qForm, first_name: e.target.value })}
                  placeholder={ts("contact.first_name") + " *"} data-testid="quote-popup-first-name"
                  className="w-full bg-ink border border-ivory/15 rounded-lg px-4 py-3 text-sm focus:border-gold focus:outline-none" />
                <input required value={qForm.last_name}
                  onChange={(e) => setQForm({ ...qForm, last_name: e.target.value })}
                  placeholder={ts("contact.last_name") + " *"} data-testid="quote-popup-last-name"
                  className="w-full bg-ink border border-ivory/15 rounded-lg px-4 py-3 text-sm focus:border-gold focus:outline-none" />
              </div>
              <input required type="email" value={qForm.email}
                onChange={(e) => setQForm({ ...qForm, email: e.target.value })}
                placeholder={ts("contact.email")} data-testid="quote-popup-email"
                className="w-full bg-ink border border-ivory/15 rounded-lg px-4 py-3 text-sm focus:border-gold focus:outline-none" />
              <input required type="number" min={101} value={qForm.qty}
                onChange={(e) => setQForm({ ...qForm, qty: e.target.value })}
                placeholder={ts("product.quote_popup_qty")} data-testid="quote-popup-qty"
                className="w-full bg-ink border border-ivory/15 rounded-lg px-4 py-3 text-sm focus:border-gold focus:outline-none" />
              <textarea rows={3} value={qForm.message}
                onChange={(e) => setQForm({ ...qForm, message: e.target.value })}
                placeholder={ts("contact.message_placeholder")} data-testid="quote-popup-message"
                className="w-full bg-ink border border-ivory/15 rounded-lg px-4 py-3 text-sm focus:border-gold focus:outline-none" />
              <button type="submit" disabled={qBusy} data-testid="quote-popup-submit"
                className="w-full inline-flex items-center justify-center gap-2 bg-gold text-ink font-bold py-3.5 rounded-full text-sm hover:shadow-[0_0_20px_rgba(212,175,55,0.35)] transition-all duration-300 disabled:opacity-50 cursor-pointer">
                {qBusy && <Loader2 className="w-4 h-4 animate-spin" />}
                {qBusy ? t("contact.sending") : t("contact.send")}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
